"use client";

import { Check, X } from "lucide-react";

interface ThingsYouShouldKnowProps {
  petsAllowed?: boolean;
  smokingAllowed?: boolean;
  festivalsEventsAllowed?: boolean;
  pickupTime?: string;
  dropoffTime?: string;
}

// Helper function to format time from 24h to 12h format
function formatTime(time?: string): string {
  if (!time) return "N/A";

  // Handle both "HH:MM" and "HH:MM:SS" formats
  const parts = time.split(":");
  if (parts.length < 2) return time; // Return original if format is invalid

  const hours = parts[0]!; // Non-null assertion since we checked length
  const minutes = parts[1] || "00";
  const hour = parseInt(hours, 10);

  if (isNaN(hour)) return time; // Return original if parsing fails

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${displayHour}:${minutes} ${period}`;
}

export function ThingsYouShouldKnow({
  petsAllowed = false,
  smokingAllowed = false,
  festivalsEventsAllowed = false,
  pickupTime = "10:00",
  dropoffTime = "18:00",
}: ThingsYouShouldKnowProps = {}) {
  return (
    <div className="py-8">
      <h2 className="text-2xl font-semibold mb-4 text-[#020817]">
        Things you should know
      </h2>

      {/* House Rules */}
      <div className="mb-8">
        <h4 className="text-base font-semibold mb-3 text-[#020817]">
          House Rules
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-12">
          {/* Pets */}
          <div className="flex items-center gap-3">
            {petsAllowed ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <X className="w-5 h-5 text-red-500" />
            )}
            <span className="text-[#020817] text-base">
              Pets {petsAllowed ? "Allowed" : "Not Allowed"}
            </span>
          </div>

          {/* Smoking */}
          <div className="flex items-center gap-3">
            {smokingAllowed ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <X className="w-5 h-5 text-red-500" />
            )}
            <span className="text-[#020817] text-base">
              Smoking {smokingAllowed ? "Allowed" : "Not Allowed"}
            </span>
          </div>

          {/* Festivals/Events */}
          <div className="flex items-center gap-3">
            {festivalsEventsAllowed ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <X className="w-5 h-5 text-red-500" />
            )}
            <span className="text-[#020817] text-base">
              Festivals/Events{" "}
              {festivalsEventsAllowed ? "Allowed" : "Not Allowed"}
            </span>
          </div>
        </div>
      </div>

      {/* Pick Up & Drop Off */}
      <div className="mb-8">
        <h4 className="text-base font-semibold mb-3 text-[#020817]">
          Pick Up & Drop Off
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pick Up */}
          <div
            className="rounded-lg p-3"
            style={{ backgroundColor: "#F3F4F6" }}
          >
            <p className="text-sm text-[#64748B] mb-1 font-medium">
              Pick Up Time
            </p>
            <p className="text-base font-medium text-[#020817]">
              {formatTime(pickupTime)}
            </p>
          </div>

          {/* Drop Off */}
          <div
            className="rounded-lg p-3"
            style={{ backgroundColor: "#F3F4F6" }}
          >
            <p className="text-sm text-[#64748B] mb-1 font-medium">
              Drop Off Time
            </p>
            <p className="text-base font-medium text-[#020817]">
              {formatTime(dropoffTime)}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      <div>
        <h4 className="text-base font-semibold mb-3 text-[#020817]">
          Payment Terms
        </h4>
        <p className="text-[#020817] text-base">100% due at booking</p>
      </div>
    </div>
  );
}
